import { useEffect, useState, type ActionDispatch } from 'react';
import { CollaborationProvider } from '../collaboration/CollaborationProvider';
import AdminSidebar from './AdminSidebar';
import Footer from './Footer';
import Tenant from './Tenant';
import IdleTimeout from './authentication/IdleTimeout';
import FeatureFlagGuard from './featureFlags/FeatureFlagGuard';
import { FeatureFlagProvider } from './featureFlags/FeatureFlagProvider';
import Header from './header/Header';
import TenantSwitcher from './masthead/TenantSwitcher';
import NotificationList from './notifications/NotificationList';
import { TabsProvider } from './tabs/TabsProvider';
import { TenantProvider } from './TenantProvider';
import { allowed } from '../sources/tenant';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import {
    useNotificationDispatch,
    type NotificationAction,
} from './notifications/NotificationProvider';
import type { UserResponseAPI, UserTenantResponseAPI } from '../types/user';
import { useUser } from './UserProvider';
import { getOrgTenantCount } from '../sources/organization';

type PendoOptions = {
    visitor: {
        id: string;
        email: string;
        full_name: string;
    };
    account: {
        id: string;
        name: string;
        expires: string;
    };
    parentAccount: {
        id: string;
        name: string | undefined;
    };
};

declare global {
    interface Window {
        pendo: {
            visitorId: string | undefined;
            identify: (options: PendoOptions) => unknown;
            initialize: (options: PendoOptions) => unknown;
        };
    }
}

const initializeTenant = async (
    tenants: UserTenantResponseAPI[] | undefined,
    navigate: NavigateFunction,
    dispatchNotification: ActionDispatch<[action: NotificationAction]>,
) => {
    if (!tenants || tenants.length === 0) {
        dispatchNotification({
            type: 'notifyError',
            payload: { error: 'You do not have access to any tenants.' },
        });
        return undefined;
    }

    const tenantIdFromUrl = window.location.pathname.split('/').filter((p) => !!p)[0];
    let tenant = tenantIdFromUrl ? tenants.find((t) => t.id === tenantIdFromUrl) : tenants[0];

    let errorMessage = '';

    // Tenant Id was specified in the url but we couldn't find a matching tenant for the logged in user
    if (!tenant && tenantIdFromUrl) {
        errorMessage = `You are not a member of the tenant with id ${tenantIdFromUrl}`;
    }

    //Check to see if the user is a member of any active tenants
    const hasActiveTenants = tenants.find((t) => !t.isExpired);
    if (!hasActiveTenants) {
        errorMessage = `Your user account doesn't have access to any active tenants. They have been either expired or deactivated.`;
        tenant = undefined;
    }

    if (tenant) {
        // For expired tenants stick an error notification on the screen, this will also pop open the tenant selector
        if (tenant.isExpired) {
            errorMessage = `Your trial expired on ${tenant.expiresAt} for tenant ${tenant.developerName} and has been deactivated.`;

            tenant = undefined;
        }

        if (tenant) {
            try {
                // Check that we have definitive access to the tenant i.e. not blocked by ip restrictions
                await allowed(tenant.id);

                // If there wasn't a tenant id in the url but the user is a member of at least one non-expired tenant then
                // navigate to the flows page
                if (!tenantIdFromUrl && hasActiveTenants) {
                    navigate(`/${hasActiveTenants.id}/flows`);
                }
            } catch (_ex) {
                errorMessage = `Your user account doesn't have access to the Tenant ${tenant.developerName}`;
                tenant = undefined;
            }

            try {
                if (tenant?.organization) {
                    tenant.organization.tenantCount = await getOrgTenantCount();
                }
            } catch (_ex) {
                errorMessage = `Could not find the number of tenants in this Organization`;
            }
        }
    }

    if (!tenant) {
        window.history.replaceState({}, document.title, '');
    }

    if (errorMessage !== '') {
        dispatchNotification({
            type: 'notifyError',
            payload: { error: errorMessage },
        });
    }
    return tenant;
};

const initializePendo = (user: UserResponseAPI, tenant: UserTenantResponseAPI) => {
    try {
        const pendo = window.pendo;

        if (!(pendo && user && tenant)) {
            return;
        }

        const options = {
            visitor: {
                id: user.id,
                email: user.email,
                full_name: `${user.firstName} ${user.lastName}`,
            },
            account: {
                id: tenant.id,
                name: tenant.developerName,
                expires: new Date(tenant.expiresAt).toISOString(),
            },
            parentAccount: {
                id: tenant.organization ? tenant.organization.id : 'no-organization',
                name: tenant.organization?.name,
            },
        };

        if (pendo.visitorId) {
            pendo.identify(options);
        } else {
            pendo.initialize(options);
        }
    } catch (error) {
        console.error('Failed to initialize Pendo:\n', error);
    }
};

/**
 *
 * @description The base component that renders all parts of the application that needs
 * to display the header and footer.
 */
const Tooling = () => {
    const [tenantIsInitialized, setTenantIsInitialized] = useState(false);
    const [isTenantSelectorOpen, setIsTenantSelectorOpen] = useState(false);
    const [tenant, setTenant] = useState<UserTenantResponseAPI | null>(null);
    const navigate = useNavigate();
    const dispatchNotification = useNotificationDispatch();

    const user = useUser();

    const onCloseTenantSelector = () => setIsTenantSelectorOpen(false);
    const openTenantSelector = () => setIsTenantSelectorOpen(true);

    useEffect(() => {
        initializeTenant(user.tenants, navigate, dispatchNotification)
            .then((tenant) => {
                setTenant(tenant ?? null);
                if (tenant) {
                    // Initialize Pendo with the user and tenant information
                    initializePendo(user, tenant);
                }
            })
            .finally(() => {
                setTenantIsInitialized(true);
            });
    }, [user, navigate, dispatchNotification]);

    const noTenantSelected = tenantIsInitialized && tenant === null;

    return (
        <TabsProvider>
            <div className="draw">
                <Header openTenantSelector={openTenantSelector} tenant={tenant} />
                {tenant !== null && <AdminSidebar tenant={tenant} />}
                {isTenantSelectorOpen || noTenantSelected ? (
                    <TenantSwitcher onClose={onCloseTenantSelector} currentTenant={tenant} />
                ) : null}
                {tenant && (
                    <FeatureFlagProvider tenantId={tenant.id}>
                        <FeatureFlagGuard>
                            <TenantProvider tenant={tenant}>
                                {tenant.isSso && <IdleTimeout />}
                                <CollaborationProvider tenantId={tenant.id}>
                                    <div className="tab-outer">
                                        <Tenant
                                            isTenantSelectorOpen={isTenantSelectorOpen}
                                            tenant={tenant}
                                        />
                                    </div>
                                </CollaborationProvider>
                            </TenantProvider>
                        </FeatureFlagGuard>
                    </FeatureFlagProvider>
                )}
                <Footer />
                <NotificationList />
            </div>
        </TabsProvider>
    );
};

export default Tooling;
